import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { first, Observable, of, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { catchError, switchMap } from 'rxjs/operators';
import { UserTimezoneInterface } from '@interfaces/timezone/user-timezone.interface';
import { CurrentTimezoneInterface } from '@enums/timezone/current-timezone.interface';
import { TimezoneStatus } from '@enums/timezone/timezone-status.enum';

@Injectable({
  providedIn: 'root',
})
export class TimezoneHttpService {
  constructor(private readonly httpClient: HttpClient) {}

  /**
   * get user timezone by timezone region name
   * @param id - e.g. America/Los_Angeles. To get timezone name you can use Intl.DateTimeFormat().resolvedOptions().timeZone
   * @param offset - e.g. 320
   */
  getTimezoneById(id: string, offset = 0): Observable<UserTimezoneInterface> {
    console.log(2);
    const url = `${environment.apiUrl}/timezone/getTimezoneById`;

    return this.httpClient
      .post<UserTimezoneInterface>(url, { id, offset })
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  /**
   * get user timezone by timezone region name
   * @param latitude - longitude coordinate
   * @param longitude - longitude coordinate
   */
  getTimezoneByCoordinates(latitude: number, longitude: number): Observable<UserTimezoneInterface> {
    const url = `${environment.apiUrl}/timezone/GetTimezoneByCoordinates/${latitude}/${longitude}`;
    console.log(1);
    return this.httpClient.get<UserTimezoneInterface>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  /**
   * Detects the user's timezone strategy based on their geolocation or other available data.
   * If geolocation access is granted, it returns the latitude and longitude.
   * If geolocation access is denied, it retrieves the timezone and offset based on system settings.
   * In case of other errors, it provides the error details.
   *
   * @return {Observable<CurrentTimezoneInterface>} An observable that emits an object containing the timezone detection status,
   * relevant data (such as coordinates, timezone, or offset), and any potential errors.
   */
  detectTimezoneStrategy(): Observable<CurrentTimezoneInterface> {
    return new Observable((observer) => {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition): void => {
          observer.next({
            status: TimezoneStatus.SUCCESS,
            coords: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
            error: null,
          });
        },
        (error: GeolocationPositionError): void => {
          if (error.code === error.PERMISSION_DENIED) {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const offset = new Date().getTimezoneOffset();

            observer.next({
              status: TimezoneStatus.PERMISSION_DENIED,
              timezone,
              offset,
              error,
            });
          } else {
            console.warn('Geolocation Error: ', error.message);

            observer.next({
              status: TimezoneStatus.ERROR,
              error,
            });
          }
        },
      );
    });
  }

  /**
   * Detects the user's timezone based on their geolocation or an alternate fallback method.
   * Utilizes a detection strategy to attempt retrieving the user's timezone coordinates
   * or other relevant information depending on the availability of permissions and geolocation services.
   *
   * @return {Observable<UserTimezoneInterface>} An observable stream emitting the detected timezone information
   * or null in case of an error.
   */
  detectUserTimezone(): Observable<UserTimezoneInterface> {
    return this.detectTimezoneStrategy().pipe(
      first(),
      switchMap((response) => {
        const { timezone, offset, coords } = response;

        if (response.status === TimezoneStatus.SUCCESS) {
          return this.getTimezoneByCoordinates(coords.latitude, coords.longitude);
        }

        return this.getTimezoneById(timezone, offset);
      }),
    );
  }
}
